import { Box, Button, SxProps, Theme } from '@mui/material';
import { ReactElement, useEffect } from 'react';
import {
  Control,
  FieldErrors,
  FieldValues,
  UnpackNestedValue,
  UseFormHandleSubmit,
} from 'react-hook-form';

/**
 * Form component is to be used with the FormHook. When using please render through the FormHook so that you have all of the
 * necessary configuration.
 **/

interface ButtonsI {
  label: string;
  action?: () => void;
  color?: string;
  type: any;
  isDisabled?: boolean;
  sx?: SxProps<Theme>;
}

export interface RenderFuncProps {
  control: Control<FieldValues, any>;
  errors: FieldErrors;
}

export interface FormProps<T> {
  render: ({ control, errors }: RenderFuncProps) => React.FC | ReactElement;
  buttons?: ButtonsI[];
  onSubmit?: (value: UnpackNestedValue<T>) => UnpackNestedValue<T>;
  control: any;
  errors: FieldErrors;
  handleSubmit: UseFormHandleSubmit<T>;
  sx?: SxProps<Theme>;
  formId?: string;
}

export const Form = <T,>(props: FormProps<T>) => {
  const renderProps = props;
  const { render, buttons, onSubmit, control, errors, handleSubmit } =
    renderProps;
  const handleFormSubmit = (values: UnpackNestedValue<T>) =>
    onSubmit && onSubmit(values);

  useEffect(() => {
    const prevent = function (event: any) {
      event.preventDefault();
    };

    // your form
    const form = document.getElementById('form');

    // attach event listener
    if (form) {
      form.addEventListener('submit', prevent, true);
    }

    return form?.removeEventListener('submit', prevent, false);
  }, []);

  const renderButtons = () => {
    return buttons?.map(
      (
        { label, action, color, type, sx = {}, isDisabled = false }: ButtonsI,
        index: number
      ) => {
        return (
          <Button
            key={index}
            disabled={isDisabled}
            type={type}
            variant="contained"
            style={{
              backgroundColor: color,
              marginRight: '5px',
            }}
            sx={sx}
            onClick={action}
          >
            {label}
          </Button>
        );
      }
    );
  };

  return (
    <Box
      id={props.formId || 'form'}
      component="form"
      onSubmit={() => handleSubmit(handleFormSubmit)()}
      sx={{ ...props.sx, position: 'relative' }}
      noValidate
    >
      {typeof render === 'function' && render({ control, errors })}
      {buttons && buttons?.length > 0 && (
        <Box
          sx={{
            padding: '10px',
            bottom: 0,
            position: 'fixed',
            display: 'flex',
            // paper === white in light mode, so light is unchanged; darkens in dark
            backgroundColor: 'background.paper',
            boxShadow: '0 -2px 5px rgba(0,0,0,0.1)',
            width: '40%',
          }}
        >
          {renderButtons()}
        </Box>
      )}
    </Box>
  );
};
